import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, X, Loader2, Wand2, Lock } from "lucide-react";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { useAuth } from "../App";
import { openPaywall } from "./Paywall";
import { isPremiumUser } from "../lib/products";

import { API } from "../config";
import { tx } from "../lib/tx";

const SUGGESTIONS = [
  "Trouve-moi une femme entre 25 et 35 ans qui aime voyager",
  "Je cherche quelqu'un de sportif près de chez moi",
  "Une personne calme qui aime la lecture et le cinéma",
  "Quelqu'un de drôle entre 30 et 45 ans"
];

export default function AISearchFilter({ onFiltersGenerated, language = "fr", compact = false }) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [generatedFilters, setGeneratedFilters] = useState(null);
  const auth = useAuth();
  const unlocked = isPremiumUser(auth?.user);

  const handleSearch = async () => {
    if (!query.trim()) return;
    
    setLoading(true);
    setError("");
    
    try {
      const token = localStorage.getItem("session_token");
      const response = await fetch(`${API}/ai/parse-search`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({ query: query.trim() })
      });
      
      const data = await response.json();
      
      if (response.status === 402) {
        setIsOpen(false);
        return;
      }
      if (response.ok && data.filters) {
        setGeneratedFilters(data.filters);
        setError("");
      } else {
        setError(data.detail || tx("Erreur lors de l'analyse"));
      }
    } catch (err) {
      console.error("AI search error:", err);
      setError(tx("Erreur de connexion"));
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    if (generatedFilters) {
      onFiltersGenerated(generatedFilters);
      setIsOpen(false);
      setQuery("");
      setGeneratedFilters(null);
    }
  };

  const handleSuggestion = (suggestion) => {
    setQuery(suggestion);
  };

  return (
    <>
      {/* AI Search Button - Compact version for inline with other filters */}
      <Button
        onClick={() => (unlocked ? setIsOpen(true) : openPaywall("ai_search"))}
        data-testid="ai-search-btn"
        variant={compact ? "outline" : "default"}
        size={compact ? "sm" : "default"}
        className={compact 
          ? "rounded-full border-violet-500/50 text-violet-400 bg-violet-500/10 hover:bg-violet-500/20"
          : "w-full h-14 rounded-2xl bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white font-bold text-base shadow-lg shadow-purple-500/30"
        }
      >
        <Wand2 className={compact ? "w-4 h-4 mr-1" : "w-5 h-5 mr-2"} />
        {compact ? tx("IA") : (language === 'fr' ? 'Recherche IA' : 'AI Search')}
        {!unlocked && <Lock className="w-3 h-3 ml-1 text-yellow-400" data-testid="ai-search-lock" />}
        {!compact && <Sparkles className="w-4 h-4 ml-2" />}
      </Button>

      {/* Modal */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center"
          >
            {/* Backdrop */}
            <div 
              className="absolute inset-0 bg-black/80 backdrop-blur-sm" 
              onClick={() => setIsOpen(false)} 
            />
            
            {/* Modal Content */}
            <motion.div
              initial={{ y: "100%", opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: "100%", opacity: 0 }}
              transition={{ type: "spring", damping: 25 }}
              className="relative w-full max-w-lg bg-background rounded-t-3xl sm:rounded-3xl p-6 max-h-[90vh] overflow-auto"
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-violet-600 to-purple-600 flex items-center justify-center">
                    <Wand2 className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">
                      {language === 'fr' ? 'Recherche IA' : 'AI Search'}
                    </h2>
                    <p className="text-sm text-muted-foreground">
                      {language === 'fr' ? 'Décrivez votre idéal' : 'Describe your ideal match'}
                    </p>
                  </div>
                </div>
                <button 
                  onClick={() => setIsOpen(false)}
                  className="p-2 rounded-full hover:bg-white/10"
                >
                  <X className="w-5 h-5 text-muted-foreground" />
                </button>
              </div>

              {/* Search Input */}
              <div className="space-y-4">
                <Textarea
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={language === 'fr' 
                    ? "Ex: Trouve-moi une femme entre 30 et 45 ans qui habite près de chez moi et qui aime rigoler et la danse..." 
                    : "Ex: Find me a woman between 30 and 45 who lives nearby and likes dancing..."
                  }
                  className="min-h-[120px] bg-muted/50 border-white/10 text-white resize-none"
                />
                
                {error && (
                  <p className="text-red-400 text-sm">{error}</p>
                )}

                <Button
                  onClick={handleSearch}
                  disabled={loading || !query.trim()}
                  className="w-full h-12 bg-gradient-to-r from-violet-600 to-purple-600 rounded-xl"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5 mr-2" />
                      {language === 'fr' ? 'Analyser ma recherche' : 'Analyze my search'}
                    </>
                  )}
                </Button>
              </div>

              {/* Suggestions */}
              {!generatedFilters && (
                <div className="mt-6">
                  <p className="text-sm text-muted-foreground mb-3">
                    {language === 'fr' ? 'Suggestions :' : 'Suggestions:'}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {SUGGESTIONS.map((suggestion, i) => (
                      <button
                        key={i}
                        onClick={() => handleSuggestion(tx(suggestion))}
                        className="px-3 py-1.5 bg-muted/50 hover:bg-muted rounded-full text-sm text-muted-foreground hover:text-white transition-colors"
                      >
                        {tx(suggestion)}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Generated Filters Preview */}
              {generatedFilters && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-6 p-4 bg-green-500/10 border border-green-500/30 rounded-xl"
                >
                  <h3 className="text-green-400 font-semibold mb-3 flex items-center gap-2">
                    <Sparkles className="w-4 h-4" />
                    {language === 'fr' ? 'Filtres générés' : 'Generated filters'}
                  </h3>
                  <div className="space-y-2 text-sm text-white/80">
                    {generatedFilters.gender && (
                      <p>{tx("• Genre :")} {generatedFilters.gender === 'female' ? tx("Femme") : generatedFilters.gender === 'male' ? tx("Homme") : tx("Tous")}</p>
                    )}
                    {(generatedFilters.min_age || generatedFilters.max_age) && (
                      <p>{tx("• Âge :")} {generatedFilters.min_age || 18} - {generatedFilters.max_age || 99} ans</p>
                    )}
                    {generatedFilters.city && (
                      <p>{tx("• Ville :")} {generatedFilters.city} {generatedFilters.city_lat ? '📍' : tx("(non trouvée)")}</p>
                    )}
                    {generatedFilters.max_distance && (
                      <p>{tx("• Distance : max")} {generatedFilters.max_distance} km</p>
                    )}
                    {generatedFilters.interests?.length > 0 && (
                      <p>{tx("• Intérêts :")} {generatedFilters.interests.join(", ")}</p>
                    )}
                    {generatedFilters.personality && (
                      <p>{tx("• Personnalité :")} {generatedFilters.personality}</p>
                    )}
                  </div>
                  
                  <Button
                    onClick={applyFilters}
                    className="w-full mt-4 bg-green-600 hover:bg-green-500"
                  >
                    {language === 'fr' ? 'Appliquer ces filtres' : 'Apply these filters'}
                  </Button>
                </motion.div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
